import { Input, RadioGroup, RadioGroupItem } from '@newfront-insurance/core-ui/v2';
import { useState } from 'react';

import { NovaScheduleEditorFooter } from './nova-schedule-editor-footer';
import { NO_ACCOUNT_SELECTED_SCHEDULE_LABEL } from '../../../../lib/nova-schedule-proposal';
import {
  type NovaSearchAccountResult,
  type SelectedAccount,
  useAccountSearch,
} from '../../../account-selector/use-account-search';

const NO_ACCOUNT_VALUE = '__no_account__';

export function NovaScheduleAccountEditor({
  selectedAccount,
  loggedInUserUuid,
  userScopes,
  disabled,
  onChange,
  onBack,
  onUpdate,
}: {
  selectedAccount: SelectedAccount;
  loggedInUserUuid: string;
  userScopes: string[];
  disabled: boolean;
  onChange: (account: SelectedAccount) => void;
  onBack: () => void;
  onUpdate: () => void;
}) {
  const [query, setQuery] = useState('');
  const { allResults, handleSearch, isLoading, results, error } = useAccountSearch({
    loggedInUserUuid,
    userScopes,
  });
  const trimmedQuery = query.trim();
  const visibleAccounts = getVisibleAccounts(
    trimmedQuery ? undefined : selectedAccount,
    trimmedQuery ? (results ?? []) : allResults,
  );
  const showEmptyState = Boolean(trimmedQuery && !isLoading && !error && visibleAccounts.length === 0);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    void handleSearch(value);
  };

  const handleValueChange = (value: string) => {
    if (value === NO_ACCOUNT_VALUE) {
      onChange(undefined);
      return;
    }

    onChange(visibleAccounts.find((account) => account.uuid === value));
  };

  return (
    <div className="flex min-h-[368px] w-full flex-col">
      <div className="flex flex-col gap-4 px-[30px] pt-5 pb-4">
        <h3 className="text-steel-500 text-[14px] leading-5 font-semibold">Accounts</h3>
        <Input
          type="search"
          aria-label="Search accounts"
          placeholder="Search accounts"
          value={query}
          disabled={disabled}
          onChange={(event) => handleQueryChange(event.target.value)}
          wrapperClassName="border-steel-100 h-10 rounded-full bg-white"
          inputClassName="text-steel-500 px-3 text-[14px] leading-5 placeholder:font-normal"
        />
      </div>

      <div className="border-steel-75 flex min-h-0 flex-1 flex-col border-b px-[31px] pb-5">
        {error ? (
          <ScheduleAccountMessage title="Accounts couldn't load" detail={error.data.errorMessage} />
        ) : isLoading ? (
          <ScheduleAccountMessage title="Searching accounts..." />
        ) : showEmptyState ? (
          <ScheduleAccountMessage
            title="No accounts found"
            detail={`We couldn't find an account matching “${trimmedQuery}”. Check for typos, or try searching again.`}
          />
        ) : (
          <RadioGroup
            aria-label="Account"
            value={selectedAccount?.uuid ?? NO_ACCOUNT_VALUE}
            disabled={disabled}
            onValueChange={handleValueChange}
            className="max-h-[160px] gap-4 overflow-y-auto"
          >
            {!trimmedQuery && (
              <ScheduleAccountOption value={NO_ACCOUNT_VALUE} label={NO_ACCOUNT_SELECTED_SCHEDULE_LABEL} />
            )}
            {visibleAccounts.map((account) => (
              <ScheduleAccountOption key={account.uuid} value={account.uuid} label={account.label} />
            ))}
          </RadioGroup>
        )}
      </div>

      <NovaScheduleEditorFooter disabled={disabled} onBack={onBack} onUpdate={onUpdate} />
    </div>
  );
}

function ScheduleAccountOption({ value, label }: { value: string; label: string }) {
  return (
    <label className="hover:bg-wtw-infinity-50 focus-within:bg-wtw-infinity-50 flex cursor-pointer items-center gap-3 rounded-[20px] px-4 py-[10px] transition-colors">
      <RadioGroupItem value={value} className="size-[18px]" />
      <span className="text-steel-400 min-w-0 flex-1 truncate text-[14px] leading-5">{label}</span>
    </label>
  );
}

function ScheduleAccountMessage({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="text-steel-400 flex min-h-[132px] flex-1 flex-col items-center justify-center gap-4 text-center text-[14px] leading-5">
      <p className="font-semibold">{title}</p>
      {detail && <p>{detail}</p>}
    </div>
  );
}

function getVisibleAccounts(
  selectedAccount: SelectedAccount,
  accounts: NovaSearchAccountResult[],
): NovaSearchAccountResult[] {
  const accountsByUuid = new Map<string, NovaSearchAccountResult>();
  if (selectedAccount) accountsByUuid.set(selectedAccount.uuid, selectedAccount);
  accounts.forEach((account) => accountsByUuid.set(account.uuid, account));
  return [...accountsByUuid.values()];
}
